import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Table, Loader, Typography, Iconography } from '@lucid/lego';
import type { FilterState } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import { ORDER_BUILDER_LABELS_ID } from '../../config/constants';
import {
  useSearchVehicles,
  useGetPrCodes,
  useGetPriceBookPrCodes,
} from '../../api/services/lupp/inventory/inventory';
import type { VehicleSearchFilters } from '../../api/services/lupp/inventory/dto.inventory';
import type { ParsedFilterOptions } from '../../api/services/lupp/inventory/transformers.inventory';
import { getInventoryColumns, type InventoryTableRow } from '../Configurator/utils';
import {
  GRAVITY_2027_ONLY_TRIMS,
  TOURING_TRIMS_MODEL_YEAR,
} from '../../api/services/lupp/inventory/filterOptions.inventory';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  MIN_SEARCH_LENGTH,
  FILTER_KEY_MAPPING,
  PACKAGE_FILTER_FIELD_MAPPING,
  COLUMN_TO_API_FIELD_MAPPING,
} from '../Configurator/constants';
import styles from './AvailableVehicles.module.scss';

/**
 * Maps each Gravity trim filter value to its API variant + bundle pair. The bundle is the
 * unique identifier per trim: Touring/Touring Plus share the AT variant while
 * Grand Touring/Grand Touring Ultimate share the GT variant.
 */
const GRAVITY_TRIM_FILTER_MAP: Record<string, { variant: string; bundle: string }> = {
  AT: { variant: 'AT', bundle: 'touring' },
  TP: { variant: 'AT', bundle: 'touring_plus' },
  GT: { variant: 'GT', bundle: 'grand_touring' },
  TU: { variant: 'GT', bundle: 'grand_touring_ultimate' },
};

function applyTrimFilter(filters: VehicleSearchFilters, value: string[], isGravity: boolean): void {
  if (!isGravity) {
    filters.variant = value.join(',');
    return;
  }

  const variants = new Set<string>();
  const bundles = new Set<string>();
  for (const trim of value) {
    const mapping = GRAVITY_TRIM_FILTER_MAP[trim];
    if (mapping) {
      variants.add(mapping.variant);
      bundles.add(mapping.bundle);
    } else {
      variants.add(trim);
    }
  }

  filters.variant = [...variants].join(',');
  if (bundles.size > 0) {
    filters.bundle = [...bundles].join(',');
  }
}

function getModelPrCodes(
  isImporter: boolean,
  isGravity: boolean,
  sources: {
    air: ParsedFilterOptions | undefined;
    gravity: ParsedFilterOptions | undefined;
    importerAir: ParsedFilterOptions | undefined;
    importerGravity: ParsedFilterOptions | undefined;
  }
): ParsedFilterOptions | undefined {
  if (isImporter) {
    return isGravity ? sources.importerGravity : sources.importerAir;
  }
  return isGravity ? sources.gravity : sources.air;
}

function getPackageField(code: string): string {
  if (code.startsWith('DHP')) return 'dynamicPackage';
  if (code.startsWith('TNP')) return 'techPackage';
  if (code.startsWith('TP')) return 'towingPackage';
  return 'vehiclePackage';
}

function applyPackagesFilter(filters: VehicleSearchFilters, value: string[]): void {
  const grouped: Record<string, string[]> = {};
  for (const code of value) {
    const field = getPackageField(code);
    (grouped[field] ??= []).push(code);
  }
  Object.entries(grouped).forEach(([field, codes]) => {
    (filters as Record<string, string>)[field] = codes.join(',');
  });
}

export type AvailableVehiclesProps = {
  /**
   * Contentful entry ID to fetch labels from.
   * Defaults to ORDER_BUILDER_LABELS_ID. Pass PROSPECTS_LABELS_ID for the manage orders page.
   */
  labelsId?: string;
  /**
   * Callback when a vehicle is selected (VIN or batch number click)
   */
  onVehicleSelect: (vehicle: InventoryTableRow) => void;
  /**
   * Callback when the table row selection changes
   */
  onSelectionChange?: (selected: InventoryTableRow[], visibleRows?: InventoryTableRow[]) => void;
  /**
   * Whether the parent is processing a vehicle selection (shows overlay spinner)
   * @default false
   */
  isVehicleSelectLoading?: boolean;
  /**
   * Error message to display in a banner above the table
   */
  error?: string | null;
  /**
   * Custom data-testid for the component
   * @default 'available-vehicles'
   */
  dataTestId?: string;
  /**
   * Custom content rendered in the table toolbar next to the search bar
   */
  toolbarContent?: React.ReactNode;
  /**
   * VINs already in the cart — rows stay visible and are marked "In Cart" but remain selectable for removal
   */
  cartVins?: string[];
  /**
   * Increment to programmatically clear all row selections (e.g. after a remove-from-cart action)
   * @default 0
   */
  selectionResetKey?: number;
};

/**
 * Reusable component for browsing and filtering available vehicles.
 * Owns search, filter, sort, pagination state and API hooks internally.
 * @component
 * @param props - Component props
 * @param props.onVehicleSelect - Callback when a vehicle row is clicked
 * @param props.isVehicleSelectLoading - Shows overlay spinner when true
 * @param props.error - Error message banner
 * @param props.dataTestId - Test ID
 * @returns JSX.Element
 *
 * @example
 * <AvailableVehicles onVehicleSelect={(vehicle) => handleSelect(vehicle)} />
 */
const AvailableVehicles: React.FC<AvailableVehiclesProps> = ({
  labelsId = ORDER_BUILDER_LABELS_ID,
  onVehicleSelect,
  onSelectionChange,
  isVehicleSelectLoading = false,
  error: externalError,
  dataTestId = 'available-vehicles',
  toolbarContent,
  cartVins,
  selectionResetKey = 0,
}) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  // Importer-specific rendering (selection checkboxes, VIN/Batch column styling, PR-code
  // source) is gated by the /me role.marketType rather than Auth0 JWT role codes, which
  // change when Auth0 roles are renamed. Mirrors the LayoutWrapper/cart pattern.
  const { userIdentity, isReady: isIdentityReady } = useUserIdentity();
  const isImporter = userIdentity?.role?.marketType === 'Importer';
  const isRoleLoading = !isIdentityReady;

  const { data: labels } = useLabels({
    id: labelsId,
    locale: convertToContentfulLocale(locale),
  });

  const [filterState, setFilterState] = useState<FilterState>({});
  const [modelFilterResetKey, setModelFilterResetKey] = useState(0);
  const modelFilterKey = modelFilterResetKey === 0 ? 'model' : `model-${modelFilterResetKey}`;
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  useEffect(() => {
    if (selectionResetKey > 0) {
      setSelectedIndices([]);
    }
  }, [selectionResetKey]);
  const [pendingTrims, setPendingTrims] = useState<string[] | undefined>(undefined);
  const [pendingModel, setPendingModel] = useState<string | undefined>(undefined);
  // null  = year explicitly cleared in the pending state (do NOT fall back to applied years)
  // undefined = user hasn't touched year in this panel session → fall back to applied state
  const [pendingModelYears, setPendingModelYears] = useState<string[] | null | undefined>(
    undefined
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const queryClient = useQueryClient();

  // Reset to first page and refetch when locale changes
  useEffect(() => {
    setCurrentPage(0);
    queryClient.invalidateQueries({ queryKey: ['VEHICLE_SEARCH'] });
  }, [locale, queryClient]);

  // Fetch prCodes for both models on initial render (Air: 2025, Gravity: 2026)
  // Importers use the LUPP price-book endpoint for Air and Gravity 2026.
  // All other roles use the CPQ endpoint.
  const { data: airPrCodes } = useGetPrCodes('air', '2025', !isRoleLoading && !isImporter);
  const { data: gravityPrCodes } = useGetPrCodes('gravity', '2026', !isRoleLoading && !isImporter);

  const { data: importerAirPrCodes } = useGetPriceBookPrCodes(
    { channel: 'IMPORTER', model: 'air', year: '2026', locale },
    !isRoleLoading && isImporter
  );

  const { data: importerGravityPrCodes } = useGetPriceBookPrCodes(
    { channel: 'IMPORTER', model: 'gravity', year: '2026', locale },
    !isRoleLoading && isImporter
  );

  // Determine current model from filter state or pending selection
  const appliedModel = filterState.model as string | undefined;
  const selectedModel = pendingModel ?? appliedModel;
  const isGravitySelected = selectedModel === 'GRAVITY';

  const prCodes = getModelPrCodes(isImporter, isGravitySelected, {
    air: airPrCodes,
    gravity: gravityPrCodes,
    importerAir: importerAirPrCodes,
    importerGravity: importerGravityPrCodes,
  });

  // Convert Table filterState to API filters
  const apiFilters = useMemo((): VehicleSearchFilters | undefined => {
    const filters: VehicleSearchFilters = {};

    Object.entries(filterState).forEach(([key, value]) => {
      if (!value) return;

      if (key === 'trim' && Array.isArray(value) && value.length > 0) {
        applyTrimFilter(filters, value, filterState.model === 'GRAVITY');
        return;
      }

      if (key === 'packages' && Array.isArray(value) && value.length > 0) {
        applyPackagesFilter(filters, value);
        return;
      }

      const apiKey = FILTER_KEY_MAPPING[key];
      if (!apiKey) return;

      if (Array.isArray(value) && value.length > 0) {
        (filters as Record<string, string>)[apiKey] = value.join(',');
      } else if (typeof value === 'string' && value) {
        (filters as Record<string, string>)[apiKey] = value;
      }
    });

    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterState]);

  // Combine apiFilters with search value
  const combinedFilters = useMemo((): VehicleSearchFilters | undefined => {
    if (searchValue.trim()) {
      const trimmedSearch = searchValue.trim();
      if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
        return { ...apiFilters, vehicleIdentifier: trimmedSearch };
      }
    }
    return apiFilters;
  }, [apiFilters, searchValue]);

  // Build sort parameters for API
  const sortBy = useMemo(() => {
    if (!sortColumn) return undefined;
    return COLUMN_TO_API_FIELD_MAPPING[sortColumn] || sortColumn;
  }, [sortColumn]);

  // Search vehicles using the Auctus API
  const {
    data: searchResult,
    isLoading,
    error: searchError,
    isFetching,
  } = useSearchVehicles({
    page: currentPage,
    size: pageSize,
    filters: combinedFilters,
    sortBy,
    sortOrder: sortDirection,
  });

  const hasData = searchResult?.data !== undefined;

  const cartVinSet = useMemo(() => new Set(cartVins ?? []), [cartVins]);

  const tableData = useMemo(() => searchResult?.data ?? [], [searchResult]);

  // Build columns with dynamic filter options from prCodes
  const appliedTrims = filterState.trim as string[] | undefined;
  const selectedTrims = pendingTrims ?? appliedTrims;
  const appliedModelYears = filterState.year as string[] | undefined;
  // null → year was explicitly cleared in the pending panel → use [] (no 2027)
  // undefined → not yet modified → fall back to the applied state
  const selectedModelYears = useMemo(
    () => (pendingModelYears === undefined ? appliedModelYears : (pendingModelYears ?? [])),
    [pendingModelYears, appliedModelYears]
  );
  const columns = useMemo(
    () =>
      getInventoryColumns(
        prCodes,
        isGravitySelected,
        selectedTrims,
        labels,
        locale,
        isImporter,
        selectedModelYears
      ),
    [prCodes, isGravitySelected, selectedTrims, labels, locale, isImporter, selectedModelYears]
  );

  // Add VIN click handler and web link to columns
  const columnsWithClickHandlers = useMemo(() => {
    return columns.map((col) => {
      if (col.key === 'model' && modelFilterKey !== 'model') {
        return {
          ...col,
          key: modelFilterKey,
          render: (_value: unknown, record: InventoryTableRow, index: number) =>
            col.render ? (
              col.render(record.model, record, index)
            ) : (
              <Typography
                variant="body"
                size="small"
                theme={theme === 'dark' ? 'light' : 'dark'}
                color="white"
              >
                {record.model ?? ''}
              </Typography>
            ),
        };
      }
      if (col.key === 'vin') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => {
            const isInCart = cartVinSet.has(record.vin);
            return (
              <div className={styles.vinCell}>
                <Typography
                  variant="body"
                  size="small"
                  theme={theme}
                  color={!isInCart && !isImporter ? 'link' : 'black'}
                  onClick={isInCart ? undefined : () => onVehicleSelect(record)}
                  className={!isInCart && !isImporter ? styles.clickableVin : undefined}
                >
                  {record.vin}
                </Typography>
                {isInCart && <span className={styles.inCartBadge}>In Cart</span>}
              </div>
            );
          },
        };
      }
      if (col.key === 'batchNumber') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => (
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color={isImporter ? 'black' : 'link'}
              onClick={() => onVehicleSelect(record)}
              className={isImporter ? undefined : styles.clickableVin}
            >
              {record.batchNumber}
            </Typography>
          ),
        };
      }
      if (col.key === 'webLink') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => {
            const webUrl = record.webLink;
            if (!webUrl) return null;
            return (
              <Box
                className={styles.webLinkIcon}
                onClick={() => window.open(webUrl, '_blank', 'noopener,noreferrer')}
              >
                <Iconography category="global" name="link" color="link" theme={theme} />
              </Box>
            );
          },
        };
      }
      return col;
    });
  }, [columns, theme, onVehicleSelect, cartVinSet, isImporter, modelFilterKey]);

  const filterStateRef = useRef(filterState);
  filterStateRef.current = filterState;

  const handleFilterChange = useCallback(
    (newFilterState: FilterState) => {
      if (filterStateRef.current.model && !newFilterState.model) {
        setModelFilterResetKey((previous) => previous + 1);
      }
      // If model was previously set but is now removed, clear all other filters too
      const baseState: FilterState =
        filterStateRef.current.model && !newFilterState.model ? {} : newFilterState;

      // Strip the 2027-only trims (TP/TU) whenever 2027 is not among the selected model years.
      // These trims are associated with the 2027 model year, so removing 2027 (via pill or
      // checkbox) removes them while leaving the common trims (Touring/Grand Touring) intact.
      const appliedYears = (baseState.year as string[] | undefined) ?? [];
      const resolvedState: FilterState = { ...baseState };
      if (Array.isArray(resolvedState.trim) && !appliedYears.includes(TOURING_TRIMS_MODEL_YEAR)) {
        const strippedTrim = (resolvedState.trim as string[]).filter(
          (t) => !GRAVITY_2027_ONLY_TRIMS.includes(t)
        );
        if (strippedTrim.length > 0) {
          resolvedState.trim = strippedTrim;
        } else {
          // Delete the key entirely — an empty array still counts in activeFilterCount
          // and causes badge/pill sync mismatch in the toolbar
          delete resolvedState.trim;
        }
      }

      setFilterState(resolvedState);
      setSelectedIndices([]);
      if (onSelectionChange) {
        onSelectionChange([]);
      }
      setPendingTrims(undefined);
      setPendingModel(undefined);
      setPendingModelYears(undefined);
      setCurrentPage(0);
    },
    [onSelectionChange]
  );

  const handlePendingFilterChange = useCallback(
    (pendingFilters: FilterState) => {
      if (filterStateRef.current.model && !pendingFilters.model) {
        handleFilterChange({});
        return;
      }
      const trimValue = pendingFilters.trim;
      let newPendingTrims = Array.isArray(trimValue) ? trimValue : undefined;

      const yearValue = pendingFilters.year;
      // When year key is absent from pendingFilters (deleted via inside-panel pill), use null to
      // signal "explicitly cleared" so selectedModelYears does not fall back to the applied year.
      // When year key is present but not an array (e.g. null from an unchecked group), treat as [].
      const newPendingModelYears: string[] | null | undefined =
        'year' in pendingFilters ? (Array.isArray(yearValue) ? yearValue : []) : null;
      setPendingModelYears(newPendingModelYears);

      // Strip the 2027-only trims (TP/TU) whenever 2027 is not among the effective year selection.
      // Removing 2027 (via pill or checkbox) removes its associated trims while keeping the
      // common trims (Touring/Grand Touring) selected.
      const newYears = newPendingModelYears ?? [];
      if (newPendingTrims && !newYears.includes(TOURING_TRIMS_MODEL_YEAR)) {
        newPendingTrims = newPendingTrims.filter((t) => !GRAVITY_2027_ONLY_TRIMS.includes(t));
      }
      setPendingTrims(newPendingTrims);

      const modelValue = pendingFilters.model;
      const newPendingModel = typeof modelValue === 'string' ? modelValue : undefined;

      // When model switches, immediately apply the change and reset ALL other filters.
      // Keeping stale filters from the previous model would produce incorrect results
      // since years, trims, colors, packages, etc. are all model-specific.
      if (newPendingModel && newPendingModel !== filterStateRef.current.model) {
        setFilterState({ model: newPendingModel });
        setPendingModel(undefined);
        setPendingModelYears(undefined);
        setCurrentPage(0);
      } else {
        setPendingModel(newPendingModel);
      }
    },
    [handleFilterChange]
  );

  const tableFilterState = useMemo((): FilterState => {
    const { model: appliedTableModel, ...otherTableFilters } = filterState;
    return appliedTableModel
      ? { ...otherTableFilters, [modelFilterKey]: appliedTableModel }
      : otherTableFilters;
  }, [filterState, modelFilterKey]);
  const normalizeTableFilters = (filters: FilterState): FilterState => {
    const model = filters[modelFilterKey];
    const otherFilters = Object.fromEntries(
      Object.entries(filters).filter(([key]) => key !== 'model' && !key.startsWith('model-'))
    );
    return model === undefined ? otherFilters : { ...otherFilters, model };
  };

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  if (searchError) {
    return (
      <Box data-testid={`${dataTestId}-error`}>
        <Box pt={8}>{labels?.['configurator.error.loading'] ?? 'Error loading inventory data'}</Box>
      </Box>
    );
  }

  return (
    <div className={styles.inventoryBrowserContainer} data-testid={dataTestId}>
      {externalError && (
        <Box className={styles.errorBanner}>
          <Typography variant="body" size="small" color="white">
            {externalError}
          </Typography>
        </Box>
      )}
      <div className={styles.tableWrapper}>
        {isLoading && !hasData ? (
          <Box className={styles.loaderWrapper}>
            <Loader theme={theme} />
          </Box>
        ) : (
          <div className={styles.tableContentWrapper}>
            <Table
              columns={columnsWithClickHandlers}
              data={tableData}
              theme={invertedTheme}
              selection={{
                enabled: isImporter,
                rows: selectedIndices,
                showExport: false,
                onChange: (indices) => {
                  setSelectedIndices(indices);
                  if (onSelectionChange) {
                    const pageOffset = currentPage * pageSize;
                    const selectedRows = indices
                      .map((i) => tableData[i - pageOffset])
                      .filter(Boolean) as InventoryTableRow[];
                    onSelectionChange(selectedRows, tableData);
                  }
                },
              }}
              columnConfig={{ enabled: true }}
              filter={{
                enabled: true,
                state: tableFilterState,
                onChange: (filters) => handleFilterChange(normalizeTableFilters(filters)),
                onPendingChange: (filters) =>
                  handlePendingFilterChange(normalizeTableFilters(filters)),
                dependentFilters: {
                  [modelFilterKey]: [
                    'year',
                    'condition',
                    'trim',
                    'packages',
                    ...Object.keys(PACKAGE_FILTER_FIELD_MAPPING),
                    'exteriorColor',
                    'interiorColor',
                    'wheels',
                    'appearance',
                    'adas',
                    'availability',
                  ],
                  trim: ['exteriorColor', 'interiorColor', 'wheels', 'appearance', 'adas'],
                },
              }}
              pagination={{
                enabled: true,
                pageSize: pageSize,
                current: currentPage,
                total: searchResult?.totalElements ?? 0,
                onChange: (page) => {
                  setCurrentPage(page);
                },
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(0);
                },
              }}
              search={{
                placeholder:
                  labels?.['configurator.search.placeholder'] ?? 'Search with VIN or Batch Number',
                value: searchValue,
                onChange: (value) => {
                  setSearchValue(value);
                  setCurrentPage(0);
                  if (value === '') {
                    queryClient.invalidateQueries({ queryKey: ['VEHICLE_SEARCH'] });
                  }
                },
              }}
              sort={{
                column: sortColumn === 'model' ? modelFilterKey : sortColumn,
                direction: sortDirection,
                onSort: (column, direction) => {
                  setSortColumn(column === modelFilterKey ? 'model' : column);
                  setSortDirection(direction);
                  setCurrentPage(0);
                },
              }}
              emptyState={{
                message:
                  labels?.['configurator.emptyState.message'] ?? 'No vehicles match your filters',
              }}
              labels={{
                manageColumns: labels?.['configurator.table.manageColumns'] ?? 'Manage Columns',
                filter: labels?.['configurator.table.filter'] ?? 'Filter',
                filterWithCount:
                  labels?.['configurator.table.filterWithCount'] ?? 'Filter ({{count}})',
                selectAll: labels?.['configurator.table.selectAll'] ?? 'Select All',
                clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All',
                columnsVisible:
                  labels?.['configurator.table.columnsVisible'] ??
                  '{{visible}} of {{total}} columns visible',
                showingItems:
                  labels?.['configurator.table.showingItems'] ??
                  'Showing {{start}}-{{end}} of {{total}} Items',
                pageOf: labels?.['configurator.table.pageOf'] ?? '{{current}} of {{total}}',
                save: labels?.['configurator.table.save'] ?? 'Save',
                apply: labels?.['configurator.table.apply'] ?? 'Apply',
                filtersTitle: labels?.['configurator.table.filtersTitle'] ?? 'Filters',
              }}
              toolbar={toolbarContent ? { content: toolbarContent } : undefined}
              gaComponent={`${dataTestId}-table`}
              dataTestId={`${dataTestId}-table`}
            />
          </div>
        )}
        {(isFetching || isVehicleSelectLoading) && !(isLoading && !hasData) && (
          <div className={styles.fetchingOverlay}>
            <Loader theme={invertedTheme} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailableVehicles;
